package com.karyadina.ui.screens.flashcards

import androidx.compose.animation.*
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.outlined.Analytics
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.karyadina.model.FlashcardPresets
import com.karyadina.model.FlashCard_Subject
import com.karyadina.model.Unit as StudyUnit
import com.karyadina.service.FlashcardService
import com.karyadina.ui.screens.flashcards.components.*
import com.karyadina.ui.components.StudyExtendedFloatingActionButton
import org.koin.compose.koinInject
import kotlinx.coroutines.launch


@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun FlashcardsScreen(
    onBackClick: () -> Unit,
    modifier: Modifier = Modifier,
    viewModel: FlashcardViewModel = remember { FlashcardViewModel() } // In real Koin: koinViewModel()
) {
    val scope = rememberCoroutineScope()
    
    val uiState by viewModel.uiState.collectAsState()
    var showAddSubjectDialog by remember { mutableStateOf(false) }
    var showStatsDetail by remember { mutableStateOf(false) }
    var quickReviewCards by remember { mutableStateOf<List<com.karyadina.model.Flashcard>?>(null) }
    var selectedPreset by remember { mutableStateOf<com.karyadina.model.FlashcardPreset?>(null) }
    
    // Navigation state - moved from child screens
    var selectedUnit by remember { mutableStateOf<StudyUnit?>(null) }
    var showReviewScreen by remember { mutableStateOf(false) }
    var selectedCardForReview by remember { mutableStateOf<com.karyadina.model.Flashcard?>(null) }
    var studyAllMode by remember { mutableStateOf(false) }
    var studyDueOnly by remember { mutableStateOf(false) }
    
    val listState = androidx.compose.foundation.lazy.rememberLazyListState()
    val fabVisible by remember {
        androidx.compose.runtime.derivedStateOf {
            listState.firstVisibleItemIndex == 0
        }
    }
    
    // Dynamic title based on navigation level
    val topAppBarTitle = when {
        uiState.selectedSubject != null && selectedUnit != null -> "${uiState.selectedSubject?.name} > ${selectedUnit?.name}"
        uiState.selectedSubject != null -> uiState.selectedSubject?.name ?: "Flashcards"
        else -> "Flashcards"
    }
    
    // Determine if we should show back button (visible when in subject or unit view)
    val showNavigationBack = uiState.selectedSubject != null
    
    // Handle back navigation logic
    val handleBackNavigation = {
        if (selectedUnit != null) {
            // From unit view -> back to subject view
            selectedUnit = null
            selectedCardForReview = null
            showReviewScreen = false
            studyAllMode = false
            studyDueOnly = false
        } else if (showNavigationBack) {
            // From subject view -> back to subjects list
            viewModel.clearSelectedSubject()
        } else {
            // Exit flashcards screen
            onBackClick()
        }
    }
    
    Scaffold(
        topBar = {
            TopAppBar(
                title = {
                    Text(topAppBarTitle)
                },
                navigationIcon = {
                    IconButton(onClick = handleBackNavigation) {
                        Icon(Icons.AutoMirrored.Filled.ArrowBack, "Back")
                    }
                },
                actions = {
                    if (showStatsDetail) {
                        // Close button for stats
                        IconButton(onClick = { showStatsDetail = false }) {
                            Icon(Icons.Filled.Close, "Close Stats")
                        }
                    } else if (uiState.selectedSubject == null) {
                        // Analytics and Add buttons for main view only
                        IconButton(onClick = { showStatsDetail = true }) {
                            Icon(Icons.Outlined.Analytics, "Statistics")
                        }
                        IconButton(onClick = { showAddSubjectDialog = true }) {
                            Icon(Icons.Filled.Add, "Add Subject")
                        }
                    }
                }
            )
        },
        floatingActionButton = {
            // Only show FAB on main subject list screen (hide on stats detail or other views)
            androidx.compose.animation.AnimatedVisibility(
                visible = uiState.subjects.isNotEmpty() && fabVisible && uiState.selectedSubject == null && quickReviewCards == null && !showStatsDetail,
                enter = androidx.compose.animation.slideInVertically(
                    initialOffsetY = { it * 2 }
                ) + androidx.compose.animation.fadeIn(),
                exit = androidx.compose.animation.slideOutVertically(
                    targetOffsetY = { it * 2 }
                ) + androidx.compose.animation.fadeOut()
            ) {
                StudyExtendedFloatingActionButton(
                    onClick = { showAddSubjectDialog = true },
                    icon = { Icon(Icons.Filled.Add, "Add") },
                    text = { Text("Add Subject") }
                )
            }
        }
    ) { padding ->
        if (showStatsDetail) {
            StatsDetailScreen(
                stats = uiState.stats,
                subjectStats = uiState.subjects.map { subject ->
                    subject.name to viewModel.getSubjectStats(subject.id)
                },
                onBackClick = { showStatsDetail = false },  // Back to Flashcards subjects list
                viewModel = viewModel,
                paddingValues = padding
            )
        } else {
            Box(
                modifier = modifier
                    .fillMaxSize()
                    .padding(padding)
                    .background(
                        Brush.verticalGradient(
                            colors = listOf(
                                MaterialTheme.colorScheme.surface,
                                MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.3f)
                            )
                        )
                    )
            ) {
                if (uiState.isLoading) {
                    CircularProgressIndicator(modifier = Modifier.align(androidx.compose.ui.Alignment.Center))
                } else if (showReviewScreen && uiState.selectedSubject != null && selectedUnit != null) {
                    // Review screen for selected card(s)
                    val cardsToReview = if (selectedCardForReview != null) {
                        listOf(selectedCardForReview!!)
                    } else if (studyAllMode) {
                        val cardsList = viewModel.getService().getCards(selectedUnit!!.id)
                        cardsList.filter { it.masteryLevel != "MASTERED" }
                    } else {
                        viewModel.getService().getDueCards(uiState.selectedSubject!!.id, selectedUnit!!.id)
                    }
                    
                    if (cardsToReview.isNotEmpty()) {
                        ReviewScreen(
                            cards = cardsToReview,
                            subject = uiState.selectedSubject!!,
                            unit = selectedUnit!!,
                            service = viewModel.getService(),
                            onComplete = {
                                showReviewScreen = false
                                studyAllMode = false
                                studyDueOnly = false
                                selectedCardForReview = null
                                // Refresh to get updated mastery levels
                                val refreshedUnit = viewModel.getService().getUnit(selectedUnit!!.id)
                                if (refreshedUnit != null) {
                                    selectedUnit = refreshedUnit
                                }
                            }
                        )
                    }
                } else if (uiState.selectedSubject != null && selectedUnit != null) {
                    // Unit detail view
                    UnitDetailScreen(
                        unit = selectedUnit!!,
                        subject = uiState.selectedSubject!!,
                        service = viewModel.getService(),
                        onBack = {
                            selectedUnit = null
                            selectedCardForReview = null
                            showReviewScreen = false
                            studyAllMode = false
                            studyDueOnly = false
                        },
                        onSelectCardForReview = { card ->
                            selectedCardForReview = card
                            showReviewScreen = true
                        },
                        onStudyAll = {
                            studyAllMode = true
                            studyDueOnly = false
                            showReviewScreen = true
                        },
                        onStudyDue = {
                            studyDueOnly = true
                            studyAllMode = false
                            showReviewScreen = true
                        }
                    )
                } else if (uiState.selectedSubject != null) {
                    // Subject detail view
                    SubjectDetailScreen(
                        subject = uiState.selectedSubject!!,
                        service = viewModel.getService(),
                        onBack = {
                            viewModel.clearSelectedSubject()
                        },
                        onSelectUnit = { unit ->
                            selectedUnit = unit
                        }
                    )
                } else if (uiState.subjects.isEmpty()) {
                    EmptyFlashcardsState(
                        onSelectPreset = { preset ->
                            selectedPreset = preset
                        },
                        onCustomSetup = { showAddSubjectDialog = true }
                    )
                } else {
                    LazyColumn(
                        state = listState,
                        modifier = Modifier.fillMaxSize(),
                        contentPadding = PaddingValues(16.dp),
                        verticalArrangement = Arrangement.spacedBy(16.dp)
                    ) {
                        // Quick Review Section
                        if (uiState.stats.cardsToReview > 0) {
                            item {
                                QuickReviewCard(
                                    cardsToReview = uiState.stats.cardsToReview,
                                    onClick = {
                                        quickReviewCards = viewModel.getService().getDueCards()
                                    }
                                )
                            }
                        }
                        
                        // Subjects List
                        item {
                            Text(
                                "My Subjects",
                                style = MaterialTheme.typography.titleLarge,
                                fontWeight = FontWeight.Bold,
                                modifier = Modifier.padding(vertical = 8.dp)
                            )
                        }
                        
                        items(uiState.subjects.size) { index ->
                            val subject = uiState.subjects[index]
                            SubjectCard(
                                subject = subject,
                                stats = viewModel.getSubjectStats(subject.id),
                                onClick = { viewModel.selectSubject(subject) },
                                onDelete = {
                                    viewModel.deleteSubject(subject.id)
                                },
                                itemIndex = index
                            )
                        }
                    }
                }
            }
        }
        
        // Add Subject Dialog
        if (showAddSubjectDialog) {
            AddSubjectDialog(
                onDismiss = { showAddSubjectDialog = false },
                onAdd = { name, examType, color ->
                    viewModel.createSubject(name, com.karyadina.model.ExamType.valueOf(examType.name), color)
                    showAddSubjectDialog = false
                }
            )
        }
        
        // Preset Confirmation Dialog
        if (selectedPreset != null) {
            PresetConfirmationDialog(
                preset = selectedPreset!!,
                onDismiss = { selectedPreset = null },
                onConfirm = {
                    viewModel.createBatchFlashcards(selectedPreset!!)
                    selectedPreset = null
                }
            )
        }
        
        // Quick Review Screen
        if (quickReviewCards != null) {
             ReviewScreen(
                 cards = quickReviewCards!!,
                 subject = FlashCard_Subject(
                     id = "all",
                     name = "Quick Review",
                     examType = "OTHER",
                     color = "#4CAF50"
                 ),
                 unit = StudyUnit(
                     id = "all",
                     subjectId = "all",
                     name = "All Due Cards",
                     order = 0,
                     totalCards = quickReviewCards!!.size,
                     masteredCards = 0
                 ),
                 service = viewModel.getService(),
                 onComplete = {
                     quickReviewCards = null
                     viewModel.refresh()
                 }
             )
        }
        }
    }
